<template>
    <div class="flex">
        <account-picker
            v-model:account-no="accountNo"
            v-model:sub-account-uuid="subAccountUuid"
            v-model:sub-account-lines="line.subAccountLines"
            :accounts
            class="w-full min-w-[90px] max-w-[300px]"
            :class="[isVatLiable ? 'min-w-[90px] max-w-[300px]': 'min-w-[150px] max-w-[420px]']"
            data-cy="credit-account-input"
            @update:account-no="onAccountUpdate"
            @on-sub-account-selection-complete="onSubAccountSelectionComplete"
        />
        <vat-picker
            v-if="isVatLiable"
            v-model:vat-option="vatOption"
            :amount
            :editable="isVatEditable"
            class="w-full min-w-[60px] max-w-[120px]"
            data-cy="credit-vat-input"
        />
    </div>
</template>

<script setup lang="ts">
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import AccountPicker from '@/account-picker/AccountPicker.vue'
import {bookkeepingAccounts, findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {findSubAccountByUuid, findSupplierByUuid, useSubAccounts} from '@/subaccount/subAccount.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {useAccounts} from '@/services/account.service'
import {isCreditVatEditable, useVatService} from '@/vat/vat.service'
import {computed, ComputedRef} from 'vue'
import VatPicker from '@/vat/VatPicker.vue'
import {useSetting} from '@/setting/setting.service'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {defaultVatOption, VatOption} from '@/vat/types/VatOptions'

const line = defineModel<TransactionEntryLine>({required: true})
const accountStore = useAccounts()
const {customers, suppliers} = useSubAccounts()
const {vatOptions} = useVatService()

const {setting} = useSetting<boolean>('VAT_LIABLE')

const accounts = computed(() => {
    return [...bookkeepingAccounts.value, ...customers.value, ...suppliers.value]
        .filter(acc => acc.active)
})

const isVatLiable: ComputedRef<Boolean> = computed<Boolean>(() => {
    const isVatLiableSetting = setting.value
    return isVatLiableSetting?.value ?? isVatLiableSetting?.default ?? isVatLiable.value
})

const onAccountUpdate = (accountNo: string | undefined) => {
    if (!accountNo) {
        line.value.vatOptionIdCredit = defaultVatOption.id
        return
    }
    if (isVatLiable.value) {
        const vatOptionCreditId = accountStore.findAccountOptionFrom(accountNo, line.value.creditSubAccount)
        line.value.vatOptionIdCredit = vatOptionCreditId?.vatSuggestionId || defaultVatOption.id
    }
}

const setSubAccountSuggestedDefaults = () => {
    if (line.value.creditSubAccount && line.value.debitAccount === undefined) {
        const supplierSubAccount = findSupplierByUuid(line.value.creditSubAccount)
        if (supplierSubAccount?.metadata?.suggestions?.expenseAccount) {
            line.value.debitAccount = supplierSubAccount.metadata.suggestions.expenseAccount
            const account = findAccountByNo(line.value.debitAccount)
            account && setSuggestedDebitVat(account)
        }
        if (supplierSubAccount?.metadata?.suggestions?.project) {
            line.value.project = supplierSubAccount.metadata.suggestions.project
        }
        if (supplierSubAccount?.metadata?.suggestions?.department) {
            line.value.department = supplierSubAccount.metadata.suggestions.department
        }
    }
}

const setSuggestedDebitVat = (account: BookkeepingAccount) => {
    if (account.vatSuggestionId) {
        line.value.vatOptionIdDebit = account.vatSuggestionId
    }
}

const accountNo = computed({
    get: () => line.value.creditAccount,
    set: (value: string | undefined) => {
        if (value) {
            const vatOptionCreditId = accountStore.findAccountOptionFrom(value, line.value.creditSubAccount)
            line.value.vatOptionIdCredit = vatOptionCreditId?.vatSuggestionId || defaultVatOption.id
        }
        line.value.creditAccount = value
    }
})

const subAccountUuid = computed({
    get: () => line.value.creditSubAccount,
    set: (value: string | undefined) => {
        if (value) {
            const subAccount = findSubAccountByUuid(value)
            if (subAccount && subAccount.bban) {
                line.value.bban = subAccount.bban
            }
        }
        line.value.creditSubAccount = value
        value && setSubAccountSuggestedDefaults()
    }
})

const onSubAccountSelectionComplete = (posts: SubAccountLine[]) => {
    setTransactionLineAmount(posts)
    if (line.value.creditSubAccount) {
        const subAccount = findSubAccountByUuid(line.value.creditSubAccount)

        if (posts.length === 1) {
            const subAccountLine = posts[0]
            line.value.invoiceUuid = subAccountLine.invoiceUuid
            line.value.kid = subAccountLine.kid
            line.value.invoiceNo = subAccountLine.invoiceNo
            line.value.bban = subAccountLine.bankAccountNo || subAccount.bban || subAccount.iban
        } else {
            line.value.bban = subAccount.bban || subAccount.iban || posts.find(post => post.bankAccountNo)?.bankAccountNo
        }
    }
}

const setTransactionLineAmount = (posts: SubAccountLine[]) => {
    line.value.amount = Math.abs(posts.reduce((acc, curr) => acc + curr.remainingAmount, 0))
}

const amount = computed(() => line.value.amount)

const vatOption = computed<VatOption | undefined>({
    get: () => vatOptions.value.find((option) => option.id === line.value.vatOptionIdCredit),
    set: (newVatOption: VatOption | undefined) => {
        if (!newVatOption) return
        line.value.vatOptionIdCredit = newVatOption.id
    }
})

const isVatEditable = computed(() => isCreditVatEditable(line.value))

</script>

<style scoped>

</style>